/**
 * Canonical selection identity utilities (frontend).
 *
 * Must match backend semantics closely so the UI can reliably locate a selection
 * by identity even when GM reorders `productSelections[]`.
 */

export const KEY_DELIM = '\u001f';

const toCleanString = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

export const normalizeToken = (value) =>
  toCleanString(value)
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();

const normalizeProgramCode = (value) => normalizeToken(value);
const normalizeVendorCode = (value) => normalizeToken(value);
const normalizeProductBrandCode = (value) => normalizeToken(value);
const normalizeReferenceId = (value) => toCleanString(value);
const SEO_PROGRAM_CODE = 'DDSSEO';

export const normalizeSelectionDesignation = (selection = {}, context = {}) => {
  if (normalizeProgramCode(context.programCode) !== SEO_PROGRAM_CODE) return '';
  const aliases = [
    selection.designation,
    selection.type,
    selection.productDesignation,
    selection.productType,
  ];
  return aliases.map(normalizeToken).find(Boolean) || '';
};

const splitMaybeCsv = (value) => {
  const raw = toCleanString(value);
  if (!raw) return [];
  if (raw.includes(',')) {
    return raw
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
  }
  return [raw];
};

export const normalizeBrandsArray = (brands) => {
  const tokens = [];
  if (Array.isArray(brands)) {
    brands.forEach((b) => {
      splitMaybeCsv(b).forEach((t) => tokens.push(normalizeToken(t)));
    });
  } else {
    splitMaybeCsv(brands).forEach((t) => tokens.push(normalizeToken(t)));
  }
  const unique = Array.from(new Set(tokens.filter(Boolean)));
  unique.sort();
  return unique;
};

const extractBrandsCandidates = (selection = {}, context = {}) => {
  const candidates = [
    selection.brandsCodes,
    selection.brandCodes,
    selection.brandCd,
    selection.productBrands,
    selection.brands,
    selection.brandName,
    selection.brandNames,
    selection.brandsDisplay,
    context.brandsCodes,
    context.brandCodes,
    context.brandCd,
    context.brandName,
    context.brandNames,
    context.brandsDisplay,
  ];
  for (const c of candidates) {
    const normalized = normalizeBrandsArray(c);
    if (normalized.length > 0) return normalized;
  }
  return [];
};

export const computeBrandsKey = (selection = {}, context = {}) => extractBrandsCandidates(selection, context).join(',');

export const computeSelectionIdentityParts = (selection = {}, context = {}) => {
  const bac = toCleanString(context.bac);
  const programCode = normalizeProgramCode(context.programCode);
  const vendorCode = normalizeVendorCode(selection.vendorCode ?? selection.vendorCd ?? selection.vendor_code);
  const productBrandCode = normalizeProductBrandCode(
    selection.productBrandCode ?? selection.productBrandCd ?? selection.productCd ?? selection.product_code
  );
  const brandsKey = computeBrandsKey(selection, context);
  const referenceId = normalizeReferenceId(selection.referenceId ?? selection.referenceID ?? selection.refId);
  const designationKey = normalizeSelectionDesignation(selection, context);

  return {
    bac,
    programCode,
    vendorCode,
    productBrandCode,
    brandsKey,
    referenceId,
    designationKey,
  };
};

export const computeSelectionKey = (selection = {}, context = {}) => {
  const parts = computeSelectionIdentityParts(selection, context);
  const safe = (v) => String(v ?? '').split(KEY_DELIM).join(' ');
  const identity = [
    safe(parts.bac),
    safe(parts.programCode),
    safe(parts.vendorCode),
    safe(parts.productBrandCode),
    safe(parts.brandsKey),
  ];
  if (parts.programCode === SEO_PROGRAM_CODE) identity.push(safe(parts.designationKey));
  identity.push(safe(parts.referenceId));
  return identity.join(KEY_DELIM);
};

